import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/utils';
import { buttonVariants } from './Button.variants';

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isToggle?: boolean;
  pressed?: boolean;
  defaultPressed?: boolean;
  onPressedChange?: (pressed: boolean) => void;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isToggle = false,
      pressed: controlledPressed,
      defaultPressed = false,
      onPressedChange,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [uncontrolledPressed, setUncontrolledPressed] =
      React.useState(defaultPressed);

    const isControlled = controlledPressed !== undefined;
    const pressed = isControlled ? controlledPressed : uncontrolledPressed;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isToggle) {
        const newPressed = !pressed;
        if (!isControlled) {
          setUncontrolledPressed(newPressed);
        }
        onPressedChange?.(newPressed);
      }
      onClick?.(e);
    };

    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        type='button'
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-pressed={isToggle ? pressed : undefined}
        onClick={handleClick}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button };
